// src/quality-management/pages/DeliveryPlanPage.tsx
// Plano de Entregas: timeline das tarefas no calendário, colorida pelo estado de cronograma.

import React, { useEffect, useMemo, useState } from 'react';
import { Task, TaskStatus } from '../services/api/types';
import { apiService } from '../services/api';
import DeliveryTimeline from '../components/DeliveryTimeline';
import DeliveryTaskTable from '../components/DeliveryTaskTable';
import { getTaskRange, startOfDay, SCHEDULE_ORDER, SCHEDULE_STYLES, summarizeSchedule } from '../utils/taskSchedule';
import { Button, Select, Input, Spinner, EmptyState, Modal } from '../../shared/components/ui';

const DAY = 86_400_000;

type RangePreset = 'auto' | 'month' | '30' | '60' | '90';

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'pending', label: 'Pendente' },
  { value: 'in_progress', label: 'Em andamento' },
  { value: 'completed', label: 'Concluída' },
  { value: 'cancelled', label: 'Cancelada' },
];

const DeliveryPlanPage: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [groupBy, setGroupBy] = useState<'project' | 'employee'>('project');
  const [preset, setPreset] = useState<RangePreset>('auto');
  const [projectFilter, setProjectFilter] = useState<string>('all');

  // edição rápida
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [editStatus, setEditStatus] = useState<TaskStatus>('pending');
  const [editProgress, setEditProgress] = useState<string>('0');
  const [saving, setSaving] = useState(false);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.tasks.getTasks();
      setTasks(Array.isArray(data) ? data : []);
    } catch (e: any) {
      console.error('Delivery plan fetch error:', e);
      setError('Não foi possível carregar as tarefas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  // Projetos disponíveis para o filtro
  const projectOptions = useMemo(() => {
    const map = new Map<number, string>();
    tasks.forEach((t) => {
      const id = t.project?.id ?? t.project_id;
      if (id != null) map.set(id, t.project?.name || `Projeto ${id}`);
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [tasks]);

  // Tarefas filtradas por projeto e que possuem datas
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      const { start, end } = getTaskRange(t);
      if (!start || !end) return false;
      if (projectFilter !== 'all') {
        const id = t.project?.id ?? t.project_id;
        if (String(id) !== projectFilter) return false;
      }
      return true;
    });
  }, [tasks, projectFilter]);

  // Tarefas da tabela detalhada: mesmo filtro de projeto do gráfico, mas
  // inclui também as tarefas sem data (exibidas com "—")
  const tableTasks = useMemo(() => {
    if (projectFilter === 'all') return tasks;
    return tasks.filter((t) => String(t.project?.id ?? t.project_id) === projectFilter);
  }, [tasks, projectFilter]);

  // Intervalo da timeline conforme o preset
  const { rangeStart, rangeEnd } = useMemo(() => {
    const today = startOfDay(new Date());
    if (preset === 'month') {
      return {
        rangeStart: new Date(today.getFullYear(), today.getMonth(), 1),
        rangeEnd: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    }
    if (preset === '30' || preset === '60' || preset === '90') {
      const n = parseInt(preset, 10);
      return { rangeStart: today, rangeEnd: new Date(today.getTime() + n * DAY) };
    }
    // auto: ajusta aos dados (com hoje sempre visível), limitado a ~180 dias
    let min = today.getTime();
    let max = today.getTime();
    filteredTasks.forEach((t) => {
      const { start, end } = getTaskRange(t);
      if (start) min = Math.min(min, start.getTime());
      if (end) max = Math.max(max, end.getTime());
    });
    // pequena folga
    min -= 2 * DAY;
    max += 2 * DAY;
    const maxSpan = 180 * DAY;
    if (max - min > maxSpan) max = min + maxSpan;
    return { rangeStart: new Date(min), rangeEnd: new Date(max) };
  }, [preset, filteredTasks]);

  const summary = useMemo(() => summarizeSchedule(filteredTasks), [filteredTasks]);

  const openEdit = (task: Task) => {
    setEditingTask(task);
    setEditStatus((task.status as TaskStatus) || 'pending');
    setEditProgress(String(task.completion_percentage ?? 0));
  };

  const saveEdit = async () => {
    if (!editingTask) return;
    setSaving(true);
    try {
      await apiService.tasks.updateTask(editingTask.id, {
        status: editStatus,
        completion_percentage: Math.max(0, Math.min(100, parseInt(editProgress) || 0)),
      });
      setEditingTask(null);
      await fetchTasks();
    } catch (e: any) {
      console.error('Quick edit error:', e);
      alert(e.message || 'Erro ao atualizar a tarefa.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="border-b border-border bg-surface-1 px-8 py-6">
        <h1 className="font-display text-2xl font-bold text-foreground">Plano de entregas</h1>
        <p className="mt-1 text-base text-foreground-muted">
          Linha do tempo das tarefas por {groupBy === 'project' ? 'projeto' : 'colaborador'}, com cores por situação de prazo
        </p>
      </div>

      <div className="px-8 py-6">
        {/* Filtros */}
        <div className="mb-5 flex flex-wrap items-end gap-3">
          <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface-2 p-1">
            <Button
              variant={groupBy === 'project' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setGroupBy('project')}
              data-testid="group-by-project"
            >
              Por projeto
            </Button>
            <Button
              variant={groupBy === 'employee' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setGroupBy('employee')}
              data-testid="group-by-employee"
            >
              Por colaborador
            </Button>
          </div>

          <Select
            label="Período"
            value={preset}
            onChange={(e) => setPreset(e.target.value as RangePreset)}
            containerClassName="w-44"
            data-testid="range-preset"
            options={[
              { value: 'auto', label: 'Ajustar aos dados' },
              { value: 'month', label: 'Este mês' },
              { value: '30', label: 'Próximos 30 dias' },
              { value: '60', label: 'Próximos 60 dias' },
              { value: '90', label: 'Próximos 90 dias' },
            ]}
          />

          <Select
            label="Projeto"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            containerClassName="w-56"
            data-testid="project-filter"
            options={[{ value: 'all', label: 'Todos os projetos' }, ...projectOptions]}
          />
        </div>

        {/* Legenda + contagens */}
        <div className="mb-5 flex flex-wrap items-center gap-4">
          {SCHEDULE_ORDER.map((state) => (
            <div key={state} className="flex items-center gap-2 text-sm">
              <span className={`inline-block h-3 w-3 rounded ${SCHEDULE_STYLES[state].bar}`} />
              <span className="text-foreground-muted">{SCHEDULE_STYLES[state].label}</span>
              <span className="font-semibold tabular-nums text-foreground">{summary[state]}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3">
            <Spinner size="lg" />
            <p className="text-sm text-foreground-muted">Carregando tarefas...</p>
          </div>
        ) : error ? (
          <EmptyState
            className="py-16"
            title="Erro ao carregar"
            description={error}
            action={<Button onClick={fetchTasks}>Tentar novamente</Button>}
          />
        ) : (
          <>
            <DeliveryTimeline
              tasks={filteredTasks}
              groupBy={groupBy}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              onTaskClick={openEdit}
            />

            {/* Tabela detalhada das tarefas exibidas no gráfico */}
            <div className="mt-8" data-testid="delivery-table-section">
              <div className="mb-3 flex items-baseline justify-between gap-3">
                <h2 className="font-display text-lg font-semibold text-foreground">Tabela detalhada</h2>
                <span className="text-sm text-foreground-muted tabular-nums">
                  {tableTasks.length} {tableTasks.length === 1 ? 'tarefa' : 'tarefas'}
                </span>
              </div>
              <DeliveryTaskTable tasks={tableTasks} onTaskClick={openEdit} />
            </div>
          </>
        )}
      </div>

      {/* Edição rápida ao clicar na barra */}
      {editingTask && (
        <Modal
          isOpen
          onClose={() => setEditingTask(null)}
          title="Atualizar tarefa"
          description={editingTask.name}
          size="sm"
          data-testid="timeline-quick-edit"
          footer={
            <>
              <Button variant="ghost" onClick={() => setEditingTask(null)} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={saveEdit} loading={saving} disabled={saving} data-testid="timeline-quick-save">
                Salvar
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <Select
              label="Status"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as TaskStatus)}
              options={STATUS_OPTIONS}
              data-testid="timeline-quick-status"
            />
            <Input
              type="number"
              min="0"
              max="100"
              step="5"
              label="Progresso (%)"
              value={editProgress}
              onChange={(e) => setEditProgress(e.target.value)}
              data-testid="timeline-quick-progress"
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default DeliveryPlanPage;
